import { useRef, useState } from "react";
import { Upload, ImageOff, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { mediaUrl, uploadMedia } from "@/lib/api";
import { toast } from "sonner";

export const ImageUploader = ({ value, onChange, label = "Image", testId = "image-uploader", aspect = "320/200" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const previewUrl = value ? mediaUrl(value) : null;

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be 5MB or smaller");
      return;
    }
    setBusy(true);
    try {
      const media = await uploadMedia(file);
      onChange?.(media.id);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testId} className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
          {label}
        </span>
        {value && (
          <button
            type="button"
            onClick={() => onChange?.(null)}
            data-testid={`${testId}-clear`}
            className="inline-flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))] hover:text-[#D94A14]"
          >
            <X className="h-3 w-3" /> Remove
          </button>
        )}
      </div>
      <div
        className="relative w-full overflow-hidden rounded-xl border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--accent))]"
        style={{ aspectRatio: aspect }}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Uploaded preview"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-[hsl(var(--muted-foreground))]">
            <ImageOff className="h-5 w-5" />
            <span className="text-xs">No image yet</span>
          </div>
        )}
      </div>
      <div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={onFile}
          className="hidden"
          data-testid={`${testId}-input`}
        />
        <Button
          type="button"
          variant="outline"
          onClick={onPick}
          disabled={busy}
          data-testid={`${testId}-button`}
          className="h-9 rounded-lg"
        >
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          {value ? "Replace image" : "Upload image"}
        </Button>
      </div>
    </div>
  );
};

export default ImageUploader;
